import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { timer } from 'rxjs';

import * as fromAssetAction from '../store/actions/delivery-assets.actions';
import * as fromReducer from '../store/reducers';
import { AsperaDownloadModel, Path } from '../models';
import { API } from '../../shared/constants/app-service-constants';

const CONNECT_SDK = '//d3gcli72yxqn2z.cloudfront.net/@ibm-aspera/connect-sdk-js/v5.0.3';

declare let AW4: any;

@Injectable({
  providedIn: 'root',
})
export class AsperaService {
  public asperaWeb: any;
  private isLoaded: any = {};
  private delay = 5000;

  constructor(private http: HttpClient, private store: Store<fromReducer.State>) {}

  init() {
    if (this.isLoaded.connectsdk || this.asperaWeb) {
      return;
    }
    this.loadLib('connectsdk', `${CONNECT_SDK}/connect-sdk.js`);
  }

  loadLib(name: string, url: string) {
    const node = document.createElement('script');
    node.src = url;
    node.type = 'text/javascript';
    node.async = true;
    node.charset = 'utf-8'; // tslint:disable-line
    node.onload = () => {
      this.isLoaded[name] = true;
      this.initAsperaconnect();
    };
    document.getElementsByTagName('head')[0].appendChild(node);
  }

  initAsperaconnect() {
    this.asperaWeb = new AW4.Connect({
      sdkLocation: CONNECT_SDK,
      minVersion: '3.8.0',
      pollingTime: 3000,
      dragDropEnabled: true,
    });
    const asperaInstaller = new AW4.ConnectInstaller({ sdkLocation: CONNECT_SDK });
    asperaInstaller.supportsInstallingExtensions = true;

    this.asperaWeb.addEventListener(AW4.Connect.EVENT.STATUS, (eventType, status) => {
      // eslint-disable-next-line default-case
      switch (status) {
        case AW4.Connect.STATUS.INITIALIZING:
          asperaInstaller.showLaunching();
          break;
        case AW4.Connect.STATUS.FAILED:
          asperaInstaller.showDownload();
          break;
        case AW4.Connect.STATUS.OUTDATED:
          asperaInstaller.showUpdate();
          break;
        case AW4.Connect.STATUS.RUNNING:
          asperaInstaller.connected();
          break;
        case AW4.Connect.STATUS.EXTENSION_INSTALL:
          asperaInstaller.showExtensionInstall();
          break;
      }
    });

    this.asperaWeb.initSession();
  }

  asperaDownload(url, id) {
    this.http.get(url).subscribe((resp: any) => {
      const { asperaTransferDetails, asperaToken, asperaFilePathList } = resp[0];
      const user = asperaFilePathList.find((item) => item.asperaUserName);
      const transferSpec = new AsperaDownloadModel(asperaTransferDetails);
      transferSpec.token = asperaToken;
      transferSpec.paths = asperaFilePathList.map((item) => ({
        destination: item.destination,
        source: item.source,
      }));
      transferSpec.remote_user = user && user.asperaUserName;
      this.asperaWeb.startTransfer(
        transferSpec,
        { allow_dialogs: false },
        {
          success: () => {
            this.store.dispatch(fromAssetAction.DownloadingSuccess({ payload: id }));
            timer(this.delay).subscribe(() =>
              this.store.dispatch(fromAssetAction.CancelDownloading({ payload: id })));
          },
          error: (err) =>
            this.store.dispatch(fromAssetAction.DownloadingFail({ payload: err.error })),
        },
      );
    });
  }

  asperaDownloadAll(options: any) {
    const { assetsIds, category, deliveryKeyCode, type, campaignTrackId } = options;
    const categoryType = category.replace('documents', 'doc').replace('images', 'img');
    // eslint-disable-next-line max-len
    let url = `${API.ASPERA_DOWNLOAD_ALL}/${deliveryKeyCode}/${categoryType}/${type}?campaignTrackId=`;

    if (Number.isInteger(campaignTrackId)) {
      url += campaignTrackId;
    }

    this.http.get(url).subscribe((respArr: any) => {
      respArr.forEach((resp: any) => {
        const { asperaTransferDetails, asperaToken, asperaFilePathList } = resp;
        const user = asperaFilePathList.find((item) => item.asperaUserName);
        const transferSpec = new AsperaDownloadModel(asperaTransferDetails);
        transferSpec.token = asperaToken;
        transferSpec.paths = this.getTransferPaths(asperaFilePathList);

        transferSpec.remote_user = user && user.asperaUserName;
        this.asperaWeb.startTransfer(
          transferSpec,
          { allow_dialogs: false },
          {
            success: () => {
              assetsIds.forEach((id: string) => {
                this.store.dispatch(fromAssetAction.DownloadingSuccess({ payload: id }));
                timer(this.delay).subscribe(() =>
                  this.store.dispatch(fromAssetAction.CancelDownloading({ payload: id })));
              });
            },
            error: (err: any) => {
              assetsIds.forEach(() =>
                this.store.dispatch(fromAssetAction.DownloadingFail({ payload: err.error })));
            },
          },
        );
      });
    });
  }

  private getTransferPaths(asperaFilePathList: any): Path[] {
    return asperaFilePathList.map((item: Path) => ({
      destination: item.destination,
      source: item.source,
    }));
  }
}
